import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";

import { useDomainServices } from "@/providers/DomainServices";

import {
  AgentThreadMessageStatus,
  AgentThreadRole,
  AgentThreadStatus,
  ClientToolSpec,
  ClientViewingContext,
  isAgentThreadTextContent,
  type AgentThreadRecord,
} from "../AgentThreadRecord";

import { activeAgentThreadQueryKeyPrefix } from "./useActiveAgentThread";

interface SendMessageParams {
  threadId: string;
  message: string;
  orgId: string;
  clientContext?: ClientViewingContext;
  clientTools?: ClientToolSpec[];
}

interface SendMessageContext {
  previousAgentThreads: Array<
    [ReadonlyArray<unknown>, AgentThreadRecord | undefined]
  >;
  isRetry?: boolean;
  optimisticMessageCreated?: string;
}

function getRetryKey(threadId: string, message: string) {
  return `${threadId}:${message}`;
}

function findLastUserMessageIndex(
  messages: AgentThreadRecord["messages"],
  messageText: string,
  options?: Partial<{
    created: string;
    status: AgentThreadMessageStatus;
  }>,
) {
  for (let idx = messages.length - 1; idx >= 0; idx--) {
    const message = messages[idx];
    const textContent = message.content.find(isAgentThreadTextContent);

    if (
      message.role !== AgentThreadRole.USER ||
      textContent?.text !== messageText
    ) {
      continue;
    }

    if (options?.created !== undefined && message.created !== options.created) {
      continue;
    }

    if (options?.status !== undefined && message.status !== options.status) {
      continue;
    }

    return idx;
  }

  return -1;
}

/**
 * Hook to send a new message to an existing thread conversation.
 *
 * After successfully sending a message, this hook will immediately refetch the thread
 * query to show the new message and start polling for the assistant response.
 *
 * @returns React Query mutation for sending messages
 */
export function useSendMessage() {
  const { agentThreadService } = useDomainServices();
  const queryClient = useQueryClient();
  const optimisticContexts = useRef(
    new WeakMap<SendMessageParams, SendMessageContext>(),
  );
  const retryParams = useRef(new Map<string, SendMessageParams>());

  const applyOptimisticUpdate = ({ threadId, message }: SendMessageParams) => {
    const queryKey = activeAgentThreadQueryKeyPrefix(threadId);

    void queryClient.cancelQueries({ queryKey });

    const previousAgentThreads = queryClient.getQueriesData<AgentThreadRecord>({
      queryKey,
    });
    const created = new Date().toISOString();

    retryParams.current.delete(getRetryKey(threadId, message));

    // React Query may keep multiple cache entries for the same threadId
    // with different query key suffixes (for example, different
    // loadMessages values). Update every variant so the optimistic
    // message appears regardless of which cache entry the UI is reading.
    for (const [cachedQueryKey] of previousAgentThreads) {
      queryClient.setQueryData<AgentThreadRecord>(
        cachedQueryKey,
        (previousAgentThread) => {
          if (previousAgentThread === undefined) {
            return previousAgentThread;
          }

          return {
            ...previousAgentThread,
            messages: [
              ...previousAgentThread.messages,
              {
                content: [{ text: message }],
                created,
                role: AgentThreadRole.USER,
                status: AgentThreadMessageStatus.COMPLETED,
              },
            ],
            status: AgentThreadStatus.ROBOTO_TURN,
            continuation_token: "",
          };
        },
      );
    }

    return {
      previousAgentThreads,
      optimisticMessageCreated: created,
    };
  };

  const applyRetryUpdate = (variables: SendMessageParams) => {
    const queryKey = activeAgentThreadQueryKeyPrefix(variables.threadId);

    void queryClient.cancelQueries({ queryKey });

    const previousAgentThreads = queryClient.getQueriesData<AgentThreadRecord>({
      queryKey,
    });
    let updatedFailedMessage = false;

    retryParams.current.delete(
      getRetryKey(variables.threadId, variables.message),
    );

    for (const [cachedQueryKey] of previousAgentThreads) {
      queryClient.setQueryData<AgentThreadRecord>(
        cachedQueryKey,
        (previousAgentThread) => {
          if (previousAgentThread === undefined) {
            return previousAgentThread;
          }

          const failedMessageIndex = findLastUserMessageIndex(
            previousAgentThread.messages,
            variables.message,
            {
              status: AgentThreadMessageStatus.FAILED,
            },
          );

          if (failedMessageIndex === -1) {
            return previousAgentThread;
          }

          updatedFailedMessage = true;

          return {
            ...previousAgentThread,
            messages: previousAgentThread.messages.map(
              (agentThreadMessage, idx) => {
                if (idx !== failedMessageIndex) {
                  return agentThreadMessage;
                }

                return {
                  ...agentThreadMessage,
                  status: AgentThreadMessageStatus.COMPLETED,
                };
              },
            ),
            status: AgentThreadStatus.ROBOTO_TURN,
            continuation_token: "",
          };
        },
      );
    }

    if (!updatedFailedMessage) {
      return applyOptimisticUpdate(variables);
    }

    return {
      previousAgentThreads,
      isRetry: true,
    };
  };

  const rollbackOptimisticUpdate = (context?: SendMessageContext) => {
    context?.previousAgentThreads.forEach(([queryKey, previousAgentThread]) => {
      queryClient.setQueryData(queryKey, previousAgentThread);
    });
  };

  const markOptimisticMessageAsFailed = (
    variables: SendMessageParams,
    context?: SendMessageContext,
  ) => {
    const queryKey = activeAgentThreadQueryKeyPrefix(variables.threadId);
    const previousAgentThreadsByKey = new Map(
      context?.previousAgentThreads.map(
        ([cachedQueryKey, previousAgentThread]) => [
          JSON.stringify(cachedQueryKey),
          previousAgentThread,
        ],
      ) ?? [],
    );

    retryParams.current.set(
      getRetryKey(variables.threadId, variables.message),
      variables,
    );

    queryClient
      .getQueriesData<AgentThreadRecord>({ queryKey })
      .forEach(([cachedQueryKey]) => {
        const previousAgentThread = previousAgentThreadsByKey.get(
          JSON.stringify(cachedQueryKey),
        );

        queryClient.setQueryData<AgentThreadRecord>(
          cachedQueryKey,
          (currentAgentThread) => {
            const fallbackAgentThread =
              previousAgentThread ?? currentAgentThread;

            if (fallbackAgentThread === undefined) {
              return fallbackAgentThread;
            }

            const failedMessageIndex =
              currentAgentThread === undefined
                ? -1
                : findLastUserMessageIndex(
                    currentAgentThread.messages,
                    variables.message,
                    {
                      created: context?.optimisticMessageCreated,
                    },
                  );

            const failedMessage = {
              content: [{ text: variables.message }],
              created:
                context?.optimisticMessageCreated ?? new Date().toISOString(),
              role: AgentThreadRole.USER,
              status: AgentThreadMessageStatus.FAILED,
            };

            if (currentAgentThread === undefined || failedMessageIndex === -1) {
              return {
                ...fallbackAgentThread,
                messages: [...fallbackAgentThread.messages, failedMessage],
                status:
                  previousAgentThread?.status ?? AgentThreadStatus.USER_TURN,
                continuation_token:
                  previousAgentThread?.continuation_token ??
                  fallbackAgentThread.continuation_token,
              };
            }

            return {
              ...currentAgentThread,
              messages: currentAgentThread.messages.map(
                (agentThreadMessage, idx) => {
                  if (idx !== failedMessageIndex) {
                    return agentThreadMessage;
                  }

                  return {
                    ...agentThreadMessage,
                    status: AgentThreadMessageStatus.FAILED,
                  };
                },
              ),
              status:
                previousAgentThread?.status ?? AgentThreadStatus.USER_TURN,
              continuation_token:
                previousAgentThread?.continuation_token ??
                currentAgentThread.continuation_token,
            };
          },
        );
      });
  };

  const mutation = useMutation<
    void,
    Error,
    SendMessageParams,
    SendMessageContext
  >({
    mutationFn: async ({
      threadId,
      message,
      orgId,
      clientContext,
      clientTools,
    }) => {
      return agentThreadService.sendMessage(threadId, message, {
        orgId,
        clientContext,
        clientTools,
      });
    },
    onMutate: (variables) => {
      const existingContext = optimisticContexts.current.get(variables);

      if (existingContext !== undefined) {
        optimisticContexts.current.delete(variables);
        return existingContext;
      }

      return applyOptimisticUpdate(variables);
    },
    onError: (_error, variables, context) => {
      if (context?.isRetry) {
        retryParams.current.set(
          getRetryKey(variables.threadId, variables.message),
          variables,
        );
        rollbackOptimisticUpdate(context);
        return;
      }

      markOptimisticMessageAsFailed(variables, context);
    },
    onSuccess: async (_, variables) => {
      // Immediately refetch the thread query to show the new message and start polling
      return queryClient.refetchQueries({
        queryKey: activeAgentThreadQueryKeyPrefix(variables.threadId),
      });
    },
  });

  const mutate: typeof mutation.mutate = (variables, options) => {
    optimisticContexts.current.set(variables, applyOptimisticUpdate(variables));
    return mutation.mutate(variables, options);
  };

  const mutateAsync: typeof mutation.mutateAsync = (variables, options) => {
    optimisticContexts.current.set(variables, applyOptimisticUpdate(variables));
    return mutation.mutateAsync(variables, options);
  };

  const retry = async (threadId: string, message: string) => {
    const variables = retryParams.current.get(getRetryKey(threadId, message));

    if (variables === undefined) {
      return undefined;
    }

    optimisticContexts.current.set(variables, applyRetryUpdate(variables));
    return mutation.mutateAsync(variables);
  };

  return {
    ...mutation,
    mutate,
    mutateAsync,
    retry,
  };
}
